import React from 'react';
import { FloatingInput } from '../../shared/custom-ui';
import FormSectionSkeleton from '../../shared/custom-ui/skeleton/FormSectionSkeleton/FormSectionSkeleton';
import usePatientSectionData from '../../hooks/usePatientSectionData';
import EditSaveCancelButtons from './EditSaveCancelButtons';

export default function CurrentMedicalInformation({ Patientid, fields }) {
    const sectionKey = 'currentMedicalInformation';

    const {
        formData,
        loading,
        updating,
        editMode,
        setEditMode,
        handleInputChange,
        handleSave,
        handleCancel,
        hasChanges
    } = usePatientSectionData(Patientid, fields, sectionKey);

    const fieldMap = [
        { label: 'Current Medications', key: 'currentMedications' },
        { label: 'Current Health Complaints or Symptoms', key: 'currentHealthComplaintsOrSymptoms' },
        { label: 'Diagnosed Medical Conditions', key: 'diagnosedMedicalConditions' },
        { label: 'Recent Lab Results and Diagnostics', key: 'recentLabResultsAndDiagnostics' },
        { label: 'Ongoing Treatments or Therapies', key: 'ongoingTreatmentsOrTherapies' },
        { label: 'Immunization Records', key: 'immunizationRecords' },
    ];

    if (loading) {
        return <FormSectionSkeleton fields={6} title="Current Medical Information" />;
    }

    return (
        <section className="flex h-full flex-col rounded-xl shadow-lg border" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-subtle)' }}>
            {/* Enhanced Header */}
            <div className="sticky top-0 z-10 border-b px-6 py-4 rounded-t-[var(--radius-lg)] flex items-center justify-between" style={{ background: 'var(--primary-25)', borderColor: 'var(--border-subtle)' }}>
                <h2 className="text-lg font-bold">Current Medical Information</h2>
            </div>

            {/* Enhanced Form Area */}
            <div
                className={`relative flex-1 px-6 py-4 ${updating ? 'overflow-hidden' : 'overflow-y-auto custom-scrollbar max-h-[calc(100vh-440px)]'}`}
            >
                <div className={`${updating ? 'pointer-events-none opacity-50' : ''}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                        {fieldMap.map(({ label, key }) => (
                            <FloatingInput
                                key={key}
                                label={label}
                                name={key}
                                value={formData?.[key] || ''}
                                onChange={handleInputChange}
                                readOnly={!editMode}
                            />
                        ))}
                    </div>
                </div>

                {/* Updating overlay */}
                {updating && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl" style={{ background: 'var(--bg-card)', opacity: 0.7 }}>
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200" style={{ borderTopColor: 'var(--primary-600)' }} />
                    </div>
                )}
            </div>

            {/* Enhanced Footer */}
            <div className="sticky bottom-0 z-10 px-6 py-4 border-t rounded-b-[var(--radius-lg)]" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)' }}>
                <EditSaveCancelButtons
                    editMode={editMode}
                    hasChanges={hasChanges}
                    updating={updating}
                    loading={loading}
                    onEdit={() => setEditMode(true)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            </div>
        </section>
    );
}
